<template>
  <div class="py-4 container-fluid">
    <div class="row mb-3">
      <div class="col-12 d-flex flex-wrap justify-content-between align-items-center">
        <div>
          <h5 class="mb-0">{{ $t('reports.absenceTitle') }}</h5>
          <p class="mb-0 text-sm text-secondary">{{ $t('reports.absenceSubtitle') }}</p>
        </div>
        <material-button variant="outline" color="dark" size="sm" :disabled="!rows.length" @click="onExport">
          <i class="material-icons text-sm me-1">download</i>{{ $t('reports.exportCsv') }}
        </material-button>
      </div>
    </div>

    <!-- Date Range Filters -->
    <div class="card mb-3">
      <div class="card-body d-flex flex-wrap align-items-start gap-3">
        <div class="d-flex flex-column align-items-center">
          <button class="btn btn-sm mb-0" :class="activeRange === 'week' ? 'btn-dark' : 'btn-outline-secondary'" @click="setRange('week')">{{ $t('timeRegistration.weekFilter') }}</button>
          <small class="text-secondary text-xs mt-1">{{ weekRangeLabel }}</small>
        </div>
        <div class="d-flex flex-column align-items-center">
          <button class="btn btn-sm mb-0" :class="activeRange === 'currentSalary' ? 'btn-dark' : 'btn-outline-secondary'" @click="setRange('currentSalary')">
            <span class="d-none d-md-inline">{{ $t('timeRegistration.currentSalaryPeriodFilter') }}</span>
            <span class="d-md-none">{{ $t('timeRegistration.currentSalaryPeriodFilterShort') }}</span>
          </button>
          <small class="text-secondary text-xs mt-1">{{ currentSalaryPeriodLabel }}</small>
        </div>
        <div class="d-flex flex-column align-items-center">
          <button class="btn btn-sm mb-0" :class="activeRange === 'lastSalary' ? 'btn-dark' : 'btn-outline-secondary'" @click="setRange('lastSalary')">
            <span class="d-none d-md-inline">{{ $t('timeRegistration.lastSalaryPeriodFilter') }}</span>
            <span class="d-md-none">{{ $t('timeRegistration.lastSalaryPeriodFilterShort') }}</span>
          </button>
          <small class="text-secondary text-xs mt-1">{{ lastSalaryPeriodLabel }}</small>
        </div>
        <div class="d-flex align-items-center gap-1">
          <input v-model="customDateFrom" type="date" class="form-control form-control-sm" style="width:145px" @change="setRange('custom')" />
          <span class="text-secondary">–</span>
          <input v-model="customDateTo" type="date" class="form-control form-control-sm" style="width:145px" @change="setRange('custom')" />
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-secondary" role="status"></div>
        </div>
        <template v-else>
          <el-table :data="rows" stripe>
            <el-table-column prop="userName" :label="$t('reports.employee')" min-width="180" fixed />
            <el-table-column v-for="type in absenceTypes" :key="type" :label="absenceTypeLabel(type)" align="center" min-width="110">
              <template #default="{ row }">{{ fmtHours(row.hoursByType[type] || 0) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.totalAbsenceHours')" align="center" min-width="120">
              <template #default="{ row }"><strong>{{ fmtHours(row.totalAbsenceHours) }}</strong></template>
            </el-table-column>
            <el-table-column :label="$t('reports.actualNormalHours')" align="center" min-width="120">
              <template #default="{ row }">{{ fmtHours(row.actualNormalHours) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.plannedNormalHours')" align="center" min-width="120">
              <template #default="{ row }">{{ fmtHours(row.plannedNormalHours) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.actualOvertimeHours')" align="center" min-width="120">
              <template #default="{ row }">{{ fmtHours(row.actualOvertimeHours) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.plannedOvertimeHours')" align="center" min-width="120">
              <template #default="{ row }">{{ fmtHours(row.plannedOvertimeHours) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.afterHours')" align="center" min-width="110">
              <template #default="{ row }">{{ fmtHours(row.actualAfterHours) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.redDayHours')" align="center" min-width="110">
              <template #default="{ row }">{{ fmtHours(row.actualRedDayHours) }}</template>
            </el-table-column>
          </el-table>
          <div class="d-flex justify-content-between align-items-center mt-3">
            <span class="text-sm text-secondary">{{ $t('reports.totalAbsenceHours') }}: <strong>{{ fmtHours(totalAbsenceHours) }}</strong></span>
            <el-pagination v-model:current-page="currentPage" :page-size="pageSize" :total="totalItems" layout="prev, pager, next" @current-change="fetchData" />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";
import MaterialButton from "@/components/MaterialButton.vue";
import { exportCsv } from "../../utils/csvExport";
import { getCustomerConfig } from "../Settings/customerConfigHelper.js";
import {
  getCurrentSalaryPeriod,
  getPreviousSalaryPeriod,
  formatSalaryPeriodLabel,
  formatWeekRangeLabel,
  getWeekRange,
  dateStr,
} from "../../utils/salaryPeriod.js";

export default {
  name: "AbsenceReport",
  components: { MaterialButton },
  data() {
    return {
      fromDate: "",
      toDate: "",
      activeRange: "week",
      customDateFrom: "",
      customDateTo: "",
      salaryPeriodStartDay: 1,
      rows: [],
      absenceTypes: [],
      totalItems: 0,
      totalAbsenceHours: 0,
      currentPage: 1,
      pageSize: 20,
      loading: true,
    };
  },
  computed: {
    localeStr() {
      const l = this.$i18n.locale;
      return (l && typeof l.value === "string") ? l.value : (typeof l === "string" ? l : "is-IS");
    },
    weekRangeLabel() {
      return formatWeekRangeLabel(new Date(), this.localeStr);
    },
    currentSalaryPeriodLabel() {
      const p = getCurrentSalaryPeriod(new Date(), this.salaryPeriodStartDay);
      return formatSalaryPeriodLabel(p, this.localeStr);
    },
    lastSalaryPeriodLabel() {
      const p = getPreviousSalaryPeriod(new Date(), this.salaryPeriodStartDay);
      return formatSalaryPeriodLabel(p, this.localeStr);
    },
  },
  async mounted() {
    const salaryDayCfg = await getCustomerConfig("SalaryPeriodStartDay");
    const sd = Number(salaryDayCfg);
    this.salaryPeriodStartDay = Number.isFinite(sd) && sd >= 1 && sd <= 31 ? Math.floor(sd) : 1;
    this.setRange("week");
  },
  methods: {
    setRange(range) {
      this.activeRange = range;
      const today = new Date();

      if (range === "week") {
        const w = getWeekRange(today);
        this.fromDate = dateStr(w.from);
        this.toDate = dateStr(w.to);
      } else if (range === "currentSalary") {
        const p = getCurrentSalaryPeriod(today, this.salaryPeriodStartDay);
        if (!p) return;
        this.fromDate = dateStr(p.from);
        this.toDate = p.to ? dateStr(p.to) : dateStr(today);
      } else if (range === "lastSalary") {
        const p = getPreviousSalaryPeriod(today, this.salaryPeriodStartDay);
        if (!p) return;
        this.fromDate = dateStr(p.from);
        this.toDate = p.to ? dateStr(p.to) : dateStr(today);
      } else if (range === "custom") {
        if (!this.customDateFrom || !this.customDateTo) return;
        this.fromDate = this.customDateFrom;
        this.toDate = this.customDateTo;
      }

      this.currentPage = 1;
      this.fetchData();
    },
    fmtHours(v) {
      const n = Number(v) || 0;
      return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
    },
    absenceTypeLabel(type) {
      const key = `timesheetEntryTypes.${type.charAt(0).toLowerCase()}${type.slice(1)}`;
      const translated = this.$t(key);
      return translated === key ? type : translated;
    },
    async fetchData() {
      this.loading = true;
      try {
        const response = await api.get("/Analytics/Timesheet/Absence", {
          params: { fromDate: this.fromDate, toDate: this.toDate, currentPage: this.currentPage, pageSize: this.pageSize },
        });
        this.rows = response.data.result || [];
        this.absenceTypes = response.data.absenceTypes || [];
        this.totalItems = response.data.totalItems || 0;
        this.totalAbsenceHours = response.data.totalAbsenceHours || 0;
      } catch (e) {
        console.error("AbsenceReport fetchData error:", e); // eslint-disable-line no-console
        this.$toast.error(this.$t("common.unableToRetrieveData"), { timeout: 0 });
      } finally {
        this.loading = false;
      }
    },
    onExport() {
      const columns = [
        { key: "userName", label: this.$t("reports.employee") },
        ...this.absenceTypes.map((t) => ({ key: t, label: this.absenceTypeLabel(t) })),
        { key: "totalAbsenceHours", label: this.$t("reports.totalAbsenceHours") },
        { key: "actualNormalHours", label: this.$t("reports.actualNormalHours") },
        { key: "plannedNormalHours", label: this.$t("reports.plannedNormalHours") },
        { key: "actualOvertimeHours", label: this.$t("reports.actualOvertimeHours") },
        { key: "plannedOvertimeHours", label: this.$t("reports.plannedOvertimeHours") },
        { key: "afterHours", label: this.$t("reports.afterHours") },
        { key: "redDayHours", label: this.$t("reports.redDayHours") },
      ];
      const flatRows = this.rows.map((r) => ({
        userName: r.userName,
        totalAbsenceHours: r.totalAbsenceHours,
        actualNormalHours: r.actualNormalHours,
        plannedNormalHours: r.plannedNormalHours,
        actualOvertimeHours: r.actualOvertimeHours,
        plannedOvertimeHours: r.plannedOvertimeHours,
        afterHours: r.actualAfterHours,
        redDayHours: r.actualRedDayHours,
        ...r.hoursByType,
      }));
      exportCsv(`absence-${this.fromDate}_${this.toDate}`, columns, flatRows);
    },
  },
};
</script>
